import React, { useMemo, useCallback, useContext } from 'react';
import styled from 'styled-components';
import { isEmpty } from 'lodash';
import useFilters from '@ubeya/shared/hooks/account/useFilters';
import useFields from '@ubeya/shared/hooks/account/useFields';
import { formatOptions, removeFilter } from '../../utils/filters';
import ConfigContext from '../../contexts/ConfigContext';
import { GENERAL_FILTERS_SLUGS, PROJECT_SCHEDULING_FILTERS_SLUGS } from '../../constants';
import { FlexMiddle } from '../Flex';
import Chip from './Chip';
import PositionChip from './CustomChips/PositionsChip';
import RatingChip from './CustomChips/RatingChip';
import EmployeeTypeChip from './CustomChips/EmployeeTypeChip';
import AdminChip from './CustomChips/AdminChip';
import TagsChip from './CustomChips/TagsChip';
import ClientsChip from './CustomChips/ClientsChip';
import BranchesChip from './CustomChips/BranchesChip';
import RestrictedClientChip from './CustomChips/RestrictedClientChip';
import BookedInProjectChip from './CustomChips/BookedInProjectChip';
import SuppliersByBranchChip from './CustomChips/SuppliersByBranchChip';
import LocationsChip from './CustomChips/LocationsChip';
import AreasChip from './CustomChips/AreasChip';
import FieldChip from './CustomChips/FieldChip';
import AgeChip from './CustomChips/AgeChip';
import DateChip from './CustomChips/DateChip';
import PreferredClientsChip from './CustomChips/PreferredClientsChip';
import EventBookerChip from './CustomChips/EventBookerChip';
import WorkedWithClientFilterChip from './CustomChips/WorkedWithClientFilterChip';
import BookingStatusChip from './CustomChips/BookingStatusChip';
import BooleanChip from './CustomChips/BooleanChip';
import OrderStatusChip from './CustomChips/OrderStatusChip';
import StartTimeChip from './CustomChips/StartTimeChip';
import CostCenterChip from './CustomChips/CostCenterChip';
import SuppliersByAccountChip from './CustomChips/SuppliersByAccountChip';
import ProjectBookingStatusChip from './CustomChips/ProjectBookingStatusChip';
import ProjectBookingIssuesStatusChip from './CustomChips/ProjectBookingIssuesStatusChip';
import PayrollProcessStatusChip from './CustomChips/PayrollProcessStatusChip';
import AgeWarningChip from './CustomChips/AgeWarningChip';
import DepartmentChip from './CustomChips/DepartmentChip';
import EmploymentTypeChip from './CustomChips/EmploymentTypeChip';
import ProjectCostCenterChip from './CustomChips/ProjectCostCenterChip';
import WorkedInAreaChip from './CustomChips/WorkedInAreaChip';
import PreferredAreaChip from './CustomChips/PreferredAreaChip';
import ContractsChip from './CustomChips/ContractsChip';
import LeaveContractsChip from './CustomChips/LeaveContractsChip';

const COMPONENTS_MAP = {
  positions: PositionChip,
  rating: RatingChip,
  employeeType: EmployeeTypeChip,
  workedWithClient: WorkedWithClientFilterChip,
  adminType: AdminChip,
  tags: TagsChip,
  eventBooker: EventBookerChip,
  clients: ClientsChip,
  suppliers: SuppliersByBranchChip,
  branches: BranchesChip,
  locations: LocationsChip,
  department: DepartmentChip,
  employmentType: EmploymentTypeChip,
  workedInArea: WorkedInAreaChip,
  preferredArea: PreferredAreaChip,
  restrictedClient: RestrictedClientChip,
  bookedInProjects: BookedInProjectChip,
  payrollProcessStatus: PayrollProcessStatusChip,
  age: AgeChip,
  hasApp: BooleanChip,
  photoVerified: BooleanChip,
  approved: BooleanChip,
  clientApproval: BooleanChip,
  notBookedOnDate: DateChip,
  preferredClients: PreferredClientsChip,
  bookingStatus: BookingStatusChip,
  availableByTimeAvailability: BooleanChip,
  notExceedingEarningsLimit: BooleanChip,
  orderStatusFilter: OrderStatusChip,
  costCenter: CostCenterChip,

  [PROJECT_SCHEDULING_FILTERS_SLUGS.positions]: PositionChip,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.locations]: LocationsChip,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.mainLocations]: LocationsChip,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.areas]: AreasChip,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.startTime]: StartTimeChip,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.endTime]: StartTimeChip,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.suppliers]: SuppliersByAccountChip,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.branchCostCenters]: ProjectCostCenterChip,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.bookingStatus]: ProjectBookingStatusChip,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.bookingIssues]: ProjectBookingIssuesStatusChip,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.ageWarning]: AgeWarningChip,

  [GENERAL_FILTERS_SLUGS.WITH_CLOCK_IN]: BooleanChip,
  [GENERAL_FILTERS_SLUGS.WITH_CLOCK_OUT]: BooleanChip,
  [GENERAL_FILTERS_SLUGS.WITH_CHECK_IN]: BooleanChip,
  [GENERAL_FILTERS_SLUGS.WITH_CHECK_OUT]: BooleanChip,
  [GENERAL_FILTERS_SLUGS.WITH_BREAKS]: BooleanChip,
  [GENERAL_FILTERS_SLUGS.CONTRACT]: ContractsChip,
  [GENERAL_FILTERS_SLUGS.LEAVE_CONTRACT]: LeaveContractsChip
};

const Wrapper = styled(FlexMiddle)`
  flex: 1;
  flex-wrap: wrap;
  gap: 10px;
`;

const OptionsWrapper = styled(FlexMiddle)`
  flex-wrap: wrap;
  gap: 10px;
  .filters-row-chip-wrapper {
    margin-bottom: 0;
  }
`;

const Input = styled.input`
  background: transparent;
  border: none;
  min-height: 24px;
  flex: 1;

  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
  background: transparent;
  border: none;
  cursor: pointer;

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey3};
  }

  &:disabled {
    cursor: no-drop;
  }

  &:focus,
  &:focus-visible,
  &:hover:not(:focus, :disabled) {
    border: none;
    outline: none;
  }
`;

const MockFiltersRow = ({
  selectedKey,
  filters,
  setFilters,
  disabledFilters = [],
  className,
  search,
  setSearch,
  onBlur,
  isOpen,
  externalOptions,
  ...restProps
}) => {
  const { t } = useContext(ConfigContext);
  const { filters: data } = useFilters();
  const { mappedFields } = useFields();

  const options = useMemo(() => {
    const basicOptions = formatOptions({ options: data[selectedKey], type: 'basic', filters });

    const fieldsOptions = formatOptions({
      options: data[selectedKey],
      type: 'fields',
      formatSlug: (id) => mappedFields?.[id]?.name,
      filters
    });

    return [...basicOptions, ...fieldsOptions].filter(({ value }) => Number.isInteger(value) || !isEmpty(value));
  }, [data, filters, mappedFields, selectedKey]);

  const handleRemoveFilter = useCallback(
    ({ type, id }) => {
      setFilters(removeFilter({ type, id, allFilters: filters }));
    },
    [filters, setFilters]
  );

  return (
    <Wrapper className={className}>
      {options.length > 0 && (
        <OptionsWrapper>
          {options.map(({ slug, value, type, id }, index) => {
            const isDisabled = disabledFilters.includes(slug);
            const Component = type === 'basic' ? COMPONENTS_MAP[slug] : FieldChip;
            return (
              <Chip
                key={index}
                label={t(slug)}
                value={
                  Component ? (
                    <Component id={id} value={value} externalOptions={externalOptions} />
                  ) : Array.isArray(value) ? (
                    value.join(',')
                  ) : (
                    value
                  )
                }
                onRemove={
                  isDisabled
                    ? undefined
                    : (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleRemoveFilter({ type, id });
                      }
                }
                isDisabled={isDisabled}
              />
            );
          })}
        </OptionsWrapper>
      )}
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t('search') + '...'}
        onBlur={(e) => {
          onBlur?.(e);
          !isOpen && setSearch('');
        }}
        {...restProps}
      />
    </Wrapper>
  );
};

export default MockFiltersRow;
